import api from '@services/api';
import {
  BuyApi,
  ProductTransactablePairDto,
  ProductTransactablePairDtoTransactTypeEnum,
  ProductsApi,
  RFQTradeQuoteDataDto,
  RFQTradeQuoteSelectionDataDto,
  RFQTradeResultDto,
  SellApi,
} from '@services/generated';
import { handleErrors } from '@services/helpers';
import { rootApi } from '@store/root-api';
import appUrls from '@utils/app-urls';
import { delay } from 'utils/delay';

const API_URL = appUrls.getEndpointUrl();

const buyClient = new BuyApi(
  undefined,
  new URL(API_URL, process.env.REACT_API_URL).href,
  api,
);

const sellClient = new SellApi(
  undefined,
  new URL(API_URL, process.env.REACT_API_URL).href,
  api,
);

const productsClient = new ProductsApi(
  undefined,
  new URL(API_URL, process.env.REACT_API_URL).href,
  api,
);

interface TradeInfo {
  orderId: string;
  type: TradeType;
}

type TradeType = 'buy' | 'sell';
const tradeApi = rootApi.injectEndpoints({
  endpoints: builder => ({
    // This might be better suited in a separate api slice
    getTransactablePairs: builder.query<
      ProductTransactablePairDto[],
      {
        fromProductCode?: string;
        toProductCode?: string;
        type?: ProductTransactablePairDtoTransactTypeEnum;
      }
    >({
      queryFn: ({ fromProductCode, toProductCode, type }) =>
        handleErrors(() =>
          productsClient.get(fromProductCode, toProductCode, type),
        ),
    }),
    // Selection Data
    getQuoteData: builder.query<
      RFQTradeQuoteSelectionDataDto,
      { fromProductCode: string; toProductCode: string; type: TradeType }
    >({
      queryFn: ({ fromProductCode, toProductCode, type }) =>
        handleErrors(() =>
          type === 'buy'
            ? buyClient.getBuyQuoteData(fromProductCode, toProductCode)
            : sellClient.getSellQuoteData(fromProductCode, toProductCode),
        ),
    }),
    // Initiate Quote
    // This is incorrectly labeled as GET and should be a mutation (POST) imo
    initiateQuote: builder.mutation<
      RFQTradeQuoteDataDto,
      {
        fromProductCode: string;
        toProductCode: string;
        amount: number;
        type: TradeType;
      }
    >({
      queryFn: ({ fromProductCode, toProductCode, amount, type }) =>
        handleErrors(() =>
          type === 'buy'
            ? buyClient.getBuyQuote(fromProductCode, toProductCode, amount)
            : sellClient.getSellQuote(fromProductCode, toProductCode, amount),
        ),
    }),
    // Accept Quote
    acceptQuote: builder.mutation<RFQTradeResultDto, TradeInfo>({
      queryFn: async ({ orderId, type }) => {
        var res = await handleErrors(() =>
          type === 'buy'
            ? buyClient.complete(orderId)
            : sellClient.complete(orderId),
        );

        // We add a delay to make sure we can see the beautiful loading screen
        await delay(3000);

        return res;
      },
    }),
    // Refresh Quote
    refreshQuote: builder.mutation<RFQTradeQuoteDataDto, TradeInfo>({
      queryFn: ({ orderId, type }) =>
        handleErrors(() =>
          type === 'buy'
            ? buyClient.refresh(orderId)
            : sellClient.refresh(orderId),
        ),
    }),
  }),
});

export const {
  useGetTransactablePairsQuery,
  useGetQuoteDataQuery,
  useLazyGetQuoteDataQuery,
  useInitiateQuoteMutation,
  useAcceptQuoteMutation,
  useRefreshQuoteMutation,
} = tradeApi;
